import React, { useState } from "react";
import { Icon } from "./Icon.tsx";
import { Tag } from "./Tag.tsx";
import { Button } from "./Button.tsx";
import { getHostname, getBaseUrl } from "@utils/index.ts";
import { useUI } from "@/contexts/index.ts";
import { useBookmarks } from "@/contexts/index.ts";
import type { Bookmark } from "../types/index";

interface BookmarkCardProps {
  bookmark: Bookmark;
  index: number;
  onOpen: () => void;
  onEdit: () => void;
  onFavorite: () => void;
  onCopy: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onTagClick: (tagName: string) => void;
  onSelect: (index: number, shiftKey: boolean) => void;
}

function FaviconImage({ src, size }: { src: string; size: number }) {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return <Icon name="link" size={size} />;
  }

  return (
    <span className="bookmark-favicon-wrap">
      <img
        src={src}
        alt=""
        className="bookmark-favicon-img img-loading"
        loading="lazy"
        onLoad={(e) => e.currentTarget.classList.add("img-loaded")}
        onError={() => setErrored(true)}
      />
    </span>
  );
}

export function BookmarkCard({
  bookmark,
  index,
  onOpen,
  onEdit,
  onFavorite,
  onCopy,
  onArchive,
  onDelete,
  onTagClick,
  onSelect,
}: BookmarkCardProps) {
  const { viewMode, hideFavicons } = useUI();
  const { tagMetadata, selectedBookmarks } = useBookmarks();

  const isSelected = selectedBookmarks.has(bookmark.id);

  const tagsFromString = bookmark.tags
    ? bookmark.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

  const tagEntries =
    Array.isArray(bookmark.tags_detailed) && bookmark.tags_detailed.length
      ? bookmark.tags_detailed
      : tagsFromString.map((name) => ({
          name,
          color: undefined,
          color_override: undefined,
        }));

  const hostname = getHostname(bookmark.url);
  const baseUrl = getBaseUrl(bookmark.url);
  const displayUrl = viewMode === "list" ? baseUrl : hostname;

  const shouldUseFavicon =
    !hideFavicons &&
    !!bookmark.favicon &&
    bookmark.favicon.startsWith("/favicons/");

  const favicon = shouldUseFavicon ? (
    <FaviconImage src={bookmark.favicon!} size={24} />
  ) : (
    <Icon name="link" size={24} />
  );

  const favoriteIndicator = bookmark.is_favorite ? (
    <span className="bookmark-favorite-indicator" aria-hidden="true">
      <Icon name="star-filled" size={12} />
    </span>
  ) : null;

  const tagsEl = tagEntries.length ? (
    <div className="bookmark-tags">
      {tagEntries.map((tag) => {
        const meta = tagMetadata[tag.name] ?? {};
        const color =
          (tag as { color_override?: string }).color_override ??
          tag.color ??
          meta.color ??
          "#f59e0b";
        return (
          <Tag
            key={tag.name}
            name={tag.name}
            color={color}
            onClick={() => onTagClick(tag.name)}
          />
        );
      })}
    </div>
  ) : null;

  const colorStyle = bookmark.color
    ? ({
        "--bookmark-color": bookmark.color,
        backgroundColor: bookmark.color,
      } as React.CSSProperties)
    : undefined;

  const delayClass = `delay-${index % 10}`;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't open if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest("a") ||
      target.closest("input") ||
      target.closest("label.bookmark-select") ||
      target.closest(".bookmark-actions") ||
      target.closest(".bookmark-tags")
    ) {
      return;
    }
    onOpen();
  };

  const actions = (
    <div className="bookmark-actions">
      <Button
        text="Open"
        variant="primary"
        className="bookmark-action-btn"
        icon="external"
        title="Open bookmark"
        onClick={onOpen}
      />
      <Button
        text="Edit"
        variant="secondary"
        className="bookmark-action-btn"
        icon="edit"
        title="Edit bookmark"
        onClick={onEdit}
      />
      <Button
        variant={bookmark.is_favorite ? "warning" : "ghost"}
        className="bookmark-action-btn"
        icon={bookmark.is_favorite ? "star-filled" : "star"}
        title={
          bookmark.is_favorite ? "Remove from favorites" : "Add to favorites"
        }
        onClick={onFavorite}
      />
      <Button
        variant="ghost"
        className="bookmark-action-btn"
        icon="copy"
        title="Copy link"
        onClick={onCopy}
      />
      {bookmark.is_archived ? (
        <Button
          variant="ghost"
          className="bookmark-action-btn"
          icon="unarchive"
          title="Unarchive bookmark"
          onClick={onArchive}
        />
      ) : (
        <Button
          variant="ghost"
          className="bookmark-action-btn"
          icon="archive"
          title="Archive bookmark"
          onClick={onArchive}
        />
      )}
      <Button
        variant="danger"
        className="bookmark-action-btn"
        icon="trash"
        title="Delete bookmark"
        onClick={onDelete}
      />
    </div>
  );

  return (
    <div
      className={`bookmark-card ${isSelected ? "selected" : ""} ${bookmark.color ? "has-custom-color" : ""} entrance-animation ${delayClass}`}
      data-id={bookmark.id}
      data-index={index}
      style={colorStyle}
      role="listitem"
      tabIndex={0}
      aria-label={bookmark.title}
      onClick={handleCardClick}
    >
      <label className="bookmark-select">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) =>
            onSelect(
              index,
              e.nativeEvent instanceof MouseEvent &&
                (e.nativeEvent as MouseEvent).shiftKey,
            )
          }
        />
      </label>

      {viewMode === "list" ? (
        <>
          <div className="bookmark-col bookmark-favicon">{favicon}</div>
          <div className="bookmark-col bookmark-title-tags">
            <div className="bookmark-title">
              {favoriteIndicator}
              {bookmark.title}
            </div>
            {tagsEl}
          </div>
          <div className="bookmark-col bookmark-desc-url">
            <div className="bookmark-description">
              {bookmark.description ?? ""}
            </div>
            <div className="bookmark-url" data-full-url={bookmark.url}>
              {displayUrl}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="bookmark-header">
            <div className="bookmark-favicon">{favicon}</div>
            <div className="bookmark-info">
              <div className="bookmark-title">
                {favoriteIndicator}
                {bookmark.title}
              </div>
              <div className="bookmark-url" data-full-url={bookmark.url}>
                {displayUrl}
              </div>
            </div>
          </div>
          {bookmark.description && (
            <div className="bookmark-description">{bookmark.description}</div>
          )}
          {tagsEl}
        </>
      )}

      {actions}
    </div>
  );
}
